/**
 * De soorten bestand die dit product aanneemt, als vaste lijst (R-104, U25, R-199).
 *
 * R-104 zegt dat per bijlageveld is in te stellen welke bestandstypen zijn toegestaan; de regel
 * `bestandseisen` draagt die typen als extensies. Welke soorten er überhaupt in de opslag mogen komen,
 * zegt R-104 niet, en dat is deze lijst: een eigen keuze. Wat de definitie van een veld toestaat en hier
 * niet staat, komt de opslag niet in; wat effectief mag is wat in beide staat.
 *
 * **De lijst is kort, en dat is opzet.** Elke soort hier heeft een herkenbare inhoud die de opslagdienst
 * zelf controleert, en een bestand dat zich als een van deze soorten voordoet maar het niet is, wordt
 * geweigerd. Wat er niet op staat, en waarom:
 *
 * - SVG: een SVG is een document dat een script kan dragen, en een figuur wordt in de browser van de
 *   invuller getoond.
 * - docm, xlsm en de sjablonen (dotx, dotm, xltx, xltm): een macro is uitvoerbare code, en een sjabloon
 *   is geen ingevuld document.
 * - doc en xls: de oude Office-vorm is een samengesteld binair bestand waarvan de inhoud hier niet te
 *   controleren is. Een versleuteld docx of xlsx heeft diezelfde vorm, en valt daarmee ook af.
 * - Tekst, csv, html, zip en elk uitvoerbaar bestand: tekst en csv hebben geen herkenbare inhoud, html
 *   is een pagina met script, een zip is een container met onbekende inhoud.
 *
 * Een soort toevoegen is een besluit, geen onderhoud: er hoort een controle op de inhoud bij.
 */
export const FILE_KINDS = {
  pdf: { extensies: ['pdf'], mediatype: 'application/pdf' },
  docx: { extensies: ['docx'], mediatype: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' },
  xlsx: { extensies: ['xlsx'], mediatype: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' },
  png: { extensies: ['png'], mediatype: 'image/png' },
  jpeg: { extensies: ['jpg', 'jpeg'], mediatype: 'image/jpeg' },
  gif: { extensies: ['gif'], mediatype: 'image/gif' },
  webp: { extensies: ['webp'], mediatype: 'image/webp' },
} as const satisfies Record<string, { readonly extensies: readonly string[]; readonly mediatype: string }>

export type FileKind = keyof typeof FILE_KINDS

/** De soorten die een figuur in rich text kan zijn (`docs/koppelvlak.md` hoofdstuk 4). */
export const FIGURE_KINDS = ['png', 'jpeg', 'gif', 'webp'] as const satisfies readonly FileKind[]

export function isFileKind(value: string): value is FileKind {
  return Object.hasOwn(FILE_KINDS, value)
}

/** De soort bij een mediatype, zonder parameters en ongeacht hoofdletters, of niets. */
export function fileKindOfMediatype(mediatype: string): FileKind | undefined {
  const bare = mediatype.split(';')[0]?.trim().toLowerCase() ?? ''
  for (const [kind, description] of Object.entries(FILE_KINDS)) {
    if (description.mediatype === bare) return kind as FileKind
  }
  return undefined
}

/**
 * Of een bestandsnaam bij een soort past: haar extensie hoort bij die soort.
 *
 * De extensie is wat de invuller en de regel `bestandseisen` zien, de soort is wat de inhoud is. Een
 * `begroting.pdf` met de inhoud van een docx past dus niet, ook als het veld beide toestaat: wie het
 * bestand opent, krijgt iets anders dan de naam belooft.
 */
export function fitsFileKind(fileName: string, kind: FileKind): boolean {
  const point = fileName.lastIndexOf('.')
  if (point <= 0 || point === fileName.length - 1) return false
  const extension = fileName.slice(point + 1).toLowerCase()
  return (FILE_KINDS[kind].extensies as readonly string[]).includes(extension)
}

/** Elke extensie van de lijst, in de volgorde van de lijst: wat een regel `bestandseisen` kan noemen. */
export const FILE_EXTENSIONS: readonly string[] = Object.values(FILE_KINDS).flatMap((kind) => kind.extensies)

/**
 * De typen uit een regel `bestandseisen` die niet op de lijst staan, in kleine letters. De poort weigert
 * een definitie met zo'n type, en de configurator zegt het meteen bij de invoer: een veld dat een type
 * toestaat dat de opslag nooit aanneemt, belooft de invuller iets wat niet kan.
 */
export function unlistedExtensions(typen: readonly string[]): string[] {
  return typen.map((type) => type.toLowerCase()).filter((type) => !FILE_EXTENSIONS.includes(type))
}

/** De soort die bij de extensie van een bestandsnaam hoort, of niets als die niet op de lijst staat. */
export function fileKindOfName(fileName: string): FileKind | undefined {
  for (const kind of Object.keys(FILE_KINDS) as FileKind[]) if (fitsFileKind(fileName, kind)) return kind
  return undefined
}
